import { API_BASE_URL, fetchApi } from './api';

const BASE = 'supply-agent';

export interface SupplyAgentOrderSnapshot {
  total_items: number;
  total_lines: number;
  total_amount_euros: number;
  lines_detail?: Record<string, unknown>[];
}

export interface SupplyAgentChangeReason {
  label: string;
  step?: string;
  rule_type?: string;
  rule_id?: number;
}

export interface SupplyAgentLineChange {
  product_id: number;
  sku: string;
  product_name?: string;
  supplier_id?: number;
  supplier_name?: string;
  family_name?: string;
  old_quantity: number;
  new_quantity: number;
  old_value_euros: number;
  new_value_euros: number;
  reasons?: SupplyAgentChangeReason[];
}

export interface SupplyAgentSimulationSummary {
  before_value_euros: number;
  after_value_euros: number;
  delta_value_euros: number;
  before_units: number;
  after_units: number;
  delta_units: number;
  lines_changed: number;
  applied_rules_count: number;
  applied_rule_ids?: number[];
  applied_rule_labels?: string[];
  top_reasons?: Array<{
    label: string;
    rule_type?: string;
    rule_id?: number;
    lines_affected: number;
  }>;
  strategy_explanations?: string[];
  completion_strategy?: 'coverage_layers' | 'top_sales' | 'proportional' | 'high_margin' | null;
  rule_contributions?: Array<{
    label: string;
    rule_type?: string;
    rule_id?: number;
    lines_affected: number;
    contribution: 'lines' | 'order_satisfied' | 'order_unmet' | 'active';
  }>;
}

export interface SupplyAgentFamilySupplierSlice {
  supplier_id: number;
  supplier_name: string;
  units: number;
}

export interface SupplyAgentFamilyGlobalSummary {
  family_names?: string[];
  global_units?: number;
  global_units_outside?: number;
  local_units_after?: number;
  cap?: number | null;
  cut_here?: number;
  remaining_elsewhere?: number;
  family_maximum_quantity?: number | null;
  family_minimum_quantity?: number | null;
  family_minimum_order_value?: number | null;
  global_value?: number;
  scope?: 'warehouse' | 'supplier' | string;
  by_supplier?: SupplyAgentFamilySupplierSlice[];
}

export interface SupplyAgentFamilyOverviewResponse {
  organization_id: number;
  warehouse_id: number;
  families: Array<{
    rule_id: number;
    rule_type: string;
    name: string;
    family_names: string[];
    /** Umbral (mín o máx). Alias histórico de threshold. */
    cap: number | null;
    threshold?: number | null;
    global_units: number;
    global_value?: number;
    gap_to_min?: number;
    excess_over_max?: number;
    status?: 'below_min' | 'over_max' | 'ok' | string;
    by_supplier: SupplyAgentFamilySupplierSlice[];
  }>;
  count: number;
}

export interface SupplyAgentWarehouseSupplierImpact {
  supplier_id: number;
  supplier_name: string;
  before_value_euros: number;
  residual_cap_euros: number | null;
  share_minimum_euros?: number | null;
  after_value_euros: number;
  delta_value_euros: number;
  before_units?: number;
  after_units?: number;
  delta_units?: number;
}

export interface SupplyAgentWarehouseSimulateResponse {
  status: string;
  organization_id?: number;
  warehouse_id: number;
  budget_limit_euros: number | null;
  minimum_order_euros?: number | null;
  units_cap?: number | null;
  before_value_euros: number;
  after_value_euros: number;
  delta_value_euros: number;
  excess_before_euros: number;
  gap_to_minimum_euros?: number;
  before_units?: number;
  after_units?: number;
  delta_units?: number;
  excess_before_units?: number;
  by_supplier: SupplyAgentWarehouseSupplierImpact[];
  applied_rules: Array<{
    id?: number;
    rule_type: string;
    name: string;
    amount?: number;
    units?: number;
  }>;
}

export interface SupplyAgentCommitmentSummary {
  unit: 'units' | 'EUR' | string;
  period_target: number;
  ytd: number;
  remaining: number;
  months_left: number;
  order_target: number;
  year: number;
  distribution?: string;
  current_order?: number;
  gap_to_order_target?: number;
  status?: 'fulfilled' | 'on_track' | 'needs_top_up' | string;
}

export interface SupplyAgentSimulateResponse {
  supplier_id: number;
  warehouse_id: number;
  organization_id: number;
  status: string;
  before: SupplyAgentOrderSnapshot;
  after: SupplyAgentOrderSnapshot;
  changes: SupplyAgentLineChange[];
  simulation_summary?: SupplyAgentSimulationSummary;
  applied_rules: Record<string, unknown>[];
  lines_detail: Record<string, unknown>[];
  hard_constraints_applied?: Record<string, unknown>[];
  optimization_summary?: Record<string, unknown>;
  reduction_summary?: Record<string, unknown>;
  family_quantity_summary?: SupplyAgentFamilyGlobalSummary;
  family_global_summaries?: SupplyAgentFamilyGlobalSummary[];
  commitment_summary?: SupplyAgentCommitmentSummary | null;
  policy_notes?: string[];
}

export interface SupplyAgentApplyResponse extends SupplyAgentSimulateResponse {
  applied: boolean;
  apply_target?: 'pending_order' | 'cantidad_usuario';
  dry_run?: boolean;
  reason?: string;
  pending_order_id?: number | null;
  pending_order_created?: boolean;
  adjustment_log_id?: number | null;
  updated_items?: Array<{
    product_id: number;
    sku: string;
    pending_order_item_id: number;
    previous_quantity: number | null;
    new_quantity: number;
    suggested_quantity: number;
  }>;
  removed_items?: Array<{ product_id: number }>;
  updated_lines?: Array<{
    product_id: number;
    sku: string;
    suggestion_id: number;
    previous_user_quantity: number | null;
    new_user_quantity: number;
  }>;
  skipped_lines?: Array<Record<string, unknown>>;
}

export interface SupplyAgentSummaryResponse {
  supplier_id: number;
  warehouse_id: number;
  organization_id: number;
  total_items: number;
  total_lines: number;
  total_amount_euros: number;
  lines_detail: Record<string, unknown>[];
}

export interface SupplyAgentRulesCatalogTotals {
  active_rules: number;
  suppliers_with_rules: number;
  warehouse_rules: number;
}

export interface SupplyAgentRulesCatalogSupplier {
  supplier_id: number;
  supplier_name: string;
  active_count: number;
  rules: Record<string, unknown>[];
}

export interface SupplyAgentRulesCatalog {
  warehouse_id: number;
  totals: SupplyAgentRulesCatalogTotals;
  warehouse_rules: Record<string, unknown>[];
  suppliers: SupplyAgentRulesCatalogSupplier[];
}

export const supplyAgentService = {
  async getSummary(supplierId: number, warehouseId: number): Promise<SupplyAgentSummaryResponse> {
    const params = new URLSearchParams({
      supplier_id: String(supplierId),
      warehouse_id: String(warehouseId),
    });
    return fetchApi<SupplyAgentSummaryResponse>(`${BASE}/summary/?${params.toString()}`);
  },

  async simulate(payload: {
    supplier_id: number;
    warehouse_id: number;
    ephemeral_rules?: Record<string, unknown>[];
    use_persisted_rules?: boolean;
    apply_line_box_multiples?: boolean;
    active_rule_ids?: number[] | null;
    locale?: string;
    completion_strategy?: 'coverage_layers' | 'top_sales' | 'proportional' | 'high_margin' | null;
    reduction_strategy?: 'slow_movers' | 'proportional' | null;
  }): Promise<SupplyAgentSimulateResponse> {
    return fetchApi<SupplyAgentSimulateResponse>(`${BASE}/simulate/`, {
      method: 'POST',
      body: payload,
    });
  },

  async apply(payload: {
    supplier_id: number;
    warehouse_id: number;
    ephemeral_rules?: Record<string, unknown>[];
    use_persisted_rules?: boolean;
    apply_line_box_multiples?: boolean;
    apply_target?: 'pending_order' | 'cantidad_usuario';
    dry_run?: boolean;
    locale?: string;
    completion_strategy?: 'coverage_layers' | 'top_sales' | 'proportional' | 'high_margin' | null;
  }): Promise<SupplyAgentApplyResponse> {
    return fetchApi<SupplyAgentApplyResponse>(`${BASE}/apply/`, {
      method: 'POST',
      body: {
        ...payload,
        apply_target: payload.apply_target ?? 'pending_order',
      },
    });
  },

  async listRules(
    supplierId: number | null | undefined,
    warehouseId: number,
    options?: { includeInactive?: boolean },
  ): Promise<{ rules: Record<string, unknown>[]; count: number }> {
    const params = new URLSearchParams({
      warehouse_id: String(warehouseId),
    });
    if (supplierId != null) {
      params.set('supplier_id', String(supplierId));
    }
    if (options?.includeInactive) {
      params.set('include_inactive', 'true');
    }
    return fetchApi<{ rules: Record<string, unknown>[]; count: number }>(`${BASE}/rules/?${params.toString()}`);
  },

  async rulesCatalog(
    warehouseId: number,
    options?: { includeInactive?: boolean },
  ): Promise<SupplyAgentRulesCatalog> {
    const params = new URLSearchParams({
      warehouse_id: String(warehouseId),
    });
    if (options?.includeInactive) {
      params.set('include_inactive', 'true');
    }
    return fetchApi<SupplyAgentRulesCatalog>(`${BASE}/rules/catalog/?${params.toString()}`);
  },

  async deleteRule(
    ruleId: number,
    supplierId: number | null | undefined,
    warehouseId: number,
    options?: { hard?: boolean },
  ): Promise<{ deleted: boolean; purged?: boolean; rules: Record<string, unknown>[]; count: number }> {
    const params = new URLSearchParams({
      warehouse_id: String(warehouseId),
    });
    if (supplierId != null) {
      params.set('supplier_id', String(supplierId));
    }
    if (options?.hard) {
      params.set('hard', 'true');
    }
    return fetchApi<{ deleted: boolean; purged?: boolean; rules: Record<string, unknown>[]; count: number }>(
      `${BASE}/rules/${ruleId}/?${params.toString()}`,
      { method: 'DELETE' },
    );
  },

  async purgeInactiveRules(
    supplierId: number,
    warehouseId: number,
  ): Promise<{ purged: number; rules: Record<string, unknown>[]; count: number }> {
    return fetchApi<{ purged: number; rules: Record<string, unknown>[]; count: number }>(
      `${BASE}/rules/purge-inactive/`,
      {
        method: 'POST',
        body: { supplier_id: supplierId, warehouse_id: warehouseId },
      },
    );
  },

  async familyOverview(
    warehouseId: number,
    ephemeralRules?: Record<string, unknown>[],
  ): Promise<SupplyAgentFamilyOverviewResponse> {
    if (ephemeralRules?.length) {
      return fetchApi<SupplyAgentFamilyOverviewResponse>(`${BASE}/family-overview/`, {
        method: 'POST',
        body: { warehouse_id: warehouseId, ephemeral_rules: ephemeralRules },
      });
    }
    const params = new URLSearchParams({ warehouse_id: String(warehouseId) });
    return fetchApi<SupplyAgentFamilyOverviewResponse>(
      `${BASE}/family-overview/?${params.toString()}`,
    );
  },

  async warehouseSimulate(payload: {
    warehouse_id: number;
    ephemeral_rules?: Record<string, unknown>[];
    active_rule_ids?: number[];
  }): Promise<SupplyAgentWarehouseSimulateResponse> {
    return fetchApi<SupplyAgentWarehouseSimulateResponse>(`${BASE}/warehouse-simulate/`, {
      method: 'POST',
      body: payload,
    });
  },

  async patchRule(
    ruleId: number,
    supplierId: number | null | undefined,
    warehouseId: number,
    payload: { is_active: boolean },
  ): Promise<{
    updated: boolean;
    rule: Record<string, unknown>;
    rules: Record<string, unknown>[];
    count: number;
  }> {
    const params = new URLSearchParams({
      warehouse_id: String(warehouseId),
    });
    if (supplierId != null) {
      params.set('supplier_id', String(supplierId));
    }
    return fetchApi(`${BASE}/rules/${ruleId}/?${params.toString()}`, {
      method: 'PATCH',
      body: payload,
    });
  },

  async interpret(payload: {
    message: string;
    supplier_id?: number;
    warehouse_id?: number;
    run_simulation?: boolean;
    persist_if_requested?: boolean;
    use_llm?: boolean;
    locale?: string;
  }): Promise<Record<string, unknown>> {
    return fetchApi<Record<string, unknown>>(`${BASE}/interpret/`, {
      method: 'POST',
      body: payload,
    });
  },

  async chat(payload: {
    message: string;
    supplier_id?: number;
    warehouse_id?: number;
    run_simulation?: boolean;
    persist_if_requested?: boolean;
    use_llm?: boolean;
    confirm_save?: boolean;
    pending_rules?: Record<string, unknown>[];
    locale?: string;
    conversation_history?: Array<{ role: string; content: string }>;
    signal?: AbortSignal;
  }): Promise<{ reply: string; interpretation: Record<string, unknown> }> {
    const { signal, ...body } = payload;
    return fetchApi<{ reply: string; interpretation: Record<string, unknown> }>(`${BASE}/chat/`, {
      method: 'POST',
      body,
      signal,
    });
  },

  async logisticsMasterStatus(params?: {
    supplier_id?: number | null;
  }): Promise<SupplyAgentLogisticsMasterStatus> {
    const qs =
      params?.supplier_id != null ? `?supplier_id=${encodeURIComponent(params.supplier_id)}` : '';
    return fetchApi<SupplyAgentLogisticsMasterStatus>(`${BASE}/logistics/master-status/${qs}`);
  },

  async uploadLogisticsExcel(
    file: File,
    options?: { replace_existing?: boolean },
  ): Promise<SupplyAgentLogisticsUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    if (options?.replace_existing) {
      formData.append('replace_existing', 'true');
    }
    const token = localStorage.getItem('auth_token');
    const res = await fetch(`${API_BASE_URL}/${BASE}/logistics/upload/`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    if (!res.ok) {
      const err = (await res.json().catch(() => ({}))) as { detail?: string };
      throw new Error(err.detail || res.statusText || 'Error al subir el Excel logístico');
    }
    return res.json();
  },

  async logisticsCalculate(payload: {
    mode?: 'VALIDATE' | 'SIMULATE' | 'APPLY';
    supplier_id?: number | null;
    warehouse_id?: number | null;
    load_suggested_order?: boolean;
    dry_run?: boolean;
    rule_id?: number;
    external_rule_id?: string;
    lines?: Record<string, unknown>[];
    constraint?: Record<string, unknown>;
    strategy?: Record<string, unknown>;
    /** {product_id: unidades} que sustituye la cantidad sugerida al usar `load_suggested_order`. */
    quantity_overrides?: Record<string, number>;
    /** Con `load_suggested_order`, limita el pedido a estos productos (pedido ya validado). */
    only_product_ids?: number[];
    /** Plantilla de contenedor del catálogo para esta estimación; manda sobre la de la regla del proveedor. */
    container_template_id?: number;
    /** «Ampliar cobertura de surtido» solo en esta llamada: productos con cantidad 0 pueden entrar al completar. */
    expand_assortment?: boolean;
    /** Tope de cobertura total (días) solo en esta llamada; manda sobre el de la regla del proveedor. */
    max_total_coverage_days?: number;
    /** Tope de caducidad solo en esta llamada: ningún producto crece más allá de lo que se vende en su vida útil. */
    respect_shelf_life?: boolean;
    /** Si el pedido excede el vehículo: reducir al anterior o completar el siguiente. */
    over_capacity_option?: SupplyAgentOverCapacityOption;
    /** Productos bloqueados: el ajuste no cambia sus cantidades. */
    locked_product_ids?: number[];
    /** Objetivo del ajuste; con `amount` se completa hasta un importe mínimo en vez de una ocupación. */
    objective?: SupplyAgentFillObjective;
    /** FCL (por defecto) o LCL: en LCL la capacidad es el volumen o peso contratado, no la del contenedor. */
    shipment_mode?: SupplyAgentShipmentMode;
    contracted_volume_m3?: number;
    contracted_weight_kg?: number;
    /** Carga paletizada (por defecto), a granel o mixta. */
    load_mode?: SupplyAgentLoadMode;
    /** Varios proveedores en un mismo vehículo: `supplier_id` es el del contenedor de referencia. */
    supplier_ids?: number[];
    /** Pallets objetivo con `objective: 'pallet'`; si falta, el siguiente pallet entero. */
    target_pallets?: number;
    /** Importe mínimo (€); si falta se usa el del proveedor. */
    target_amount_euros?: number;
    /** Ahorro (€) de portes o descuento al alcanzar el importe, para compararlo con el sobrestock. */
    saving_euros?: number;
    /** Idioma de los avisos y motivos que devuelve el motor (es | it). */
    locale?: string;
  }): Promise<SupplyAgentLogisticsCalculateResponse> {
    return fetchApi<SupplyAgentLogisticsCalculateResponse>(`${BASE}/logistics/calculate/`, {
      method: 'POST',
      body: payload,
    });
  },

  async logisticsListProducts(supplierId: number): Promise<{
    status: string;
    products: SupplyAgentLogisticsProduct[];
    count: number;
  }> {
    return fetchApi(`${BASE}/logistics/products/?supplier_id=${encodeURIComponent(supplierId)}`);
  },

  /** Contenedores del catálogo, entre los que se puede cambiar el de un proveedor en la estimación. */
  async logisticsContainerTemplates(): Promise<{ results: SupplyAgentContainerTemplate[] }> {
    return fetchApi(`${BASE}/logistics/unit-templates/`, { params: { kind: 'CONTAINER' } });
  },

  async logisticsPatchProductPackaging(payload: {
    product_id: number;
    supplier_id: number;
    units_per_box?: number | null;
    boxes_per_base?: number | null;
    bases_per_pallet?: number | null;
    units_per_pallet?: number | null;
    unit_volume_m3?: number | null;
    unit_weight_kg?: number | null;
  }): Promise<{ status: string; product: SupplyAgentLogisticsProduct }> {
    return fetchApi(`${BASE}/logistics/product-packaging/`, {
      method: 'PATCH',
      body: payload,
    });
  },

  async logisticsPatchProduct(
    productEqId: number,
    payload: {
      max_extra_pct?: number | null;
      max_extra_coverage_days?: number | null;
      units_per_pallet?: number | null;
      boxes_per_base?: number | null;
      bases_per_pallet?: number | null;
      pallet_family?: string;
      pallet_length_mm?: number | null;
      pallet_width_mm?: number | null;
      pallet_loaded_height_mm?: number | null;
      pallet_tare_weight_kg?: number | null;
      pallet_max_top_load_kg?: number | null;
    },
  ): Promise<{ status: string; product: SupplyAgentLogisticsProduct }> {
    return fetchApi(`${BASE}/logistics/products/${productEqId}/`, {
      method: 'PATCH',
      body: payload,
    });
  },

  async logisticsListRules(
    supplierId: number,
    options?: { include_inactive?: boolean },
  ): Promise<{ status: string; rules: SupplyAgentLogisticsRule[]; count: number }> {
    const qs = new URLSearchParams({ supplier_id: String(supplierId) });
    if (options?.include_inactive) qs.set('include_inactive', 'true');
    return fetchApi(`${BASE}/logistics/rules/?${qs.toString()}`);
  },

  async logisticsCreateRule(
    payload: SupplyAgentLogisticsRuleWritePayload & { supplier_id: number },
  ): Promise<{ status: string; rule: SupplyAgentLogisticsRule }> {
    return fetchApi(`${BASE}/logistics/rules/`, {
      method: 'POST',
      body: payload,
    });
  },

  async logisticsPatchRule(
    ruleId: number,
    payload: SupplyAgentLogisticsRuleWritePayload,
  ): Promise<{ status: string; rule: SupplyAgentLogisticsRule }> {
    return fetchApi(`${BASE}/logistics/rules/${ruleId}/`, {
      method: 'PATCH',
      body: payload,
    });
  },

  async logisticsListContainers(supplierId: number): Promise<{
    status: string;
    containers: SupplyAgentLogisticsContainer[];
    count: number;
  }> {
    return fetchApi(`${BASE}/logistics/containers/?supplier_id=${encodeURIComponent(supplierId)}`);
  },
};

export interface SupplyAgentLogisticsMasterStatus {
  status: string;
  loaded: boolean;
  supplier_id?: number | null;
  products_count: number;
  rules_count: number;
  containers_count: number;
  rule_types?: string[];
  last_updated?: string | null;
}

export interface SupplyAgentLogisticsProduct {
  id: number;
  supplier_id: number;
  product_id: number;
  product_code: string;
  product_name: string;
  active: boolean;
  pallet_family: string;
  max_extra_pct: number | null;
  max_extra_coverage_days: number | null;
  units_per_box?: number | null;
  boxes_per_base?: number | null;
  bases_per_pallet?: number | null;
  units_per_pallet?: number | null;
  pallet_length_mm?: number | null;
  pallet_width_mm?: number | null;
  pallet_loaded_height_mm?: number | null;
  pallet_tare_weight_kg?: number | null;
  pallet_max_top_load_kg?: number | null;
  updated_at?: string | null;
}

export interface SupplyAgentLogisticsRule {
  id: number;
  supplier_id: number | null;
  external_rule_id: string;
  name: string;
  active: boolean;
  constraint_type: string;
  container_code: string;
  container_template_id?: number | null;
  target_value: number | null;
  minimum_value: number | null;
  maximum_value: number | null;
  constraint_unit: string;
  adjustment_direction: string;
  optimization_strategy: string;
  max_extra_pct_default: number | null;
  max_extra_coverage_days_default: number | null;
  max_total_coverage_days: number | null;
  incompatible_product_policy: string;
  allow_mixed_products: boolean;
  priority: number;
  notes: string;
  updated_at?: string | null;
}

export interface SupplyAgentLogisticsRuleWritePayload {
  name?: string;
  external_rule_id?: string;
  active?: boolean;
  constraint_type?: string;
  container_code?: string;
  container_template_id?: number | null;
  target_value?: number | null;
  minimum_value?: number | null;
  maximum_value?: number | null;
  constraint_unit?: string;
  adjustment_direction?: string;
  optimization_strategy?: string;
  max_extra_pct_default?: number | null;
  max_extra_coverage_days_default?: number | null;
  max_total_coverage_days?: number | null;
  incompatible_product_policy?: string;
  allow_mixed_products?: boolean;
  priority?: number;
  notes?: string;
}

export interface SupplyAgentLogisticsContainer {
  id: number;
  supplier_id: number | null;
  container_code: string;
  description: string;
  active: boolean;
  internal_length_mm: number | null;
  internal_width_mm: number | null;
  internal_height_mm: number | null;
  usable_volume_m3: number | null;
  usable_payload_kg: number | null;
  minimum_fill_pct: number | null;
  maximum_fill_pct: number | null;
  capacity_binding_rule: string;
  updated_at?: string | null;
}

export interface SupplyAgentLogisticsUploadSection {
  created: number;
  updated: number;
  errors: Array<{ row?: number; error?: string }>;
  warnings: Array<{ row?: number; warning?: string }>;
}

export interface SupplyAgentLogisticsUploadResponse {
  status: string;
  products: SupplyAgentLogisticsUploadSection;
  rules: SupplyAgentLogisticsUploadSection;
  containers: SupplyAgentLogisticsUploadSection;
  sheets?: { products?: number; rules?: number; containers?: number };
  detail?: string;
}

export interface SupplyAgentLogisticsLineAdjustment {
  product_id: number;
  sku?: string;
  product_name?: string;
  pallet_family?: string;
  temperature_zone?: string;
  old_units: number;
  new_units: number;
  delta_units: number;
  reason?: string;
  reason_code?: string;
  explanation?: string;
  explanation_items?: string[];
  boxes_added?: number;
  pallet_eq_delta?: number;
  pallet_eq_before?: number | null;
  pallet_eq_after?: number | null;
  occupancy_pct_before?: number | null;
  occupancy_pct_after?: number | null;
  occupancy_basis?: string;
  rotation_value?: number;
  desired_pallets?: number;
  gap_before?: number;
  group_label?: string;
  coverage_days_before?: number | null;
  coverage_days_after?: number | null;
  supplier_id?: number | null;
  supplier_name?: string;
  unit_price?: number | null;
  volume_delta_m3?: number | null;
  weight_delta_kg?: number | null;
  amount_delta_euros?: number | null;
  /** La línea tiene demanda: cuenta para la cobertura media. */
  has_demand?: boolean;
  overstock_units_delta?: number | null;
  overstock_amount_delta_euros?: number | null;
}

/** Aportación de un proveedor a un vehículo compartido (HU8). */
export interface SupplyAgentLogisticsSupplierBreakdown {
  supplier_id: number | null;
  supplier_name: string;
  lines: number;
  units_before: number;
  units_after: number;
  volume_m3_before: number;
  volume_m3_after: number;
  weight_kg_before: number;
  weight_kg_after: number;
  amount_before: number;
  amount_after: number;
  volume_pct_before?: number | null;
  volume_pct_after?: number | null;
  weight_pct_before?: number | null;
  weight_pct_after?: number | null;
  /** Importe mínimo de pedido del proveedor (regla de compra), si lo tiene. */
  min_amount?: number | null;
  min_amount_met_before?: boolean | null;
  min_amount_met_after?: boolean | null;
}

/** Resumen antes/después del pedido completo (HU6). Los totales de una propuesta parcial se recalculan con las líneas aceptadas. */
export interface SupplyAgentLogisticsOrderSummary {
  /** Líneas con unidades y sin precio de compra: el importe y el sobrestock en € las dejan fuera. */
  lines_without_price?: number;
  amount_before?: number | null;
  amount_after?: number | null;
  covered_units_before?: number | null;
  daily_demand_total?: number | null;
  avg_coverage_days_before?: number | null;
  avg_coverage_days_after?: number | null;
  overstock_units_before?: number | null;
  overstock_units_after?: number | null;
  overstock_amount_before?: number | null;
  overstock_amount_after?: number | null;
  overstock_generated_units?: number | null;
  overstock_generated_amount?: number | null;
}

export type SupplyAgentOverCapacityOption = 'reduce' | 'complete_next';

/** Envío completo (FCL) o parcial por volumen o peso contratado (LCL). */
export type SupplyAgentShipmentMode = 'fcl' | 'lcl';

/** Carga paletizada, a granel (sin pallets) o mixta. */
export type SupplyAgentLoadMode = 'palletized' | 'bulk' | 'mixed';

/** Objetivo del ajuste: ocupación del contenedor (por defecto), importe mínimo (HU5), pallets completos o bases completas (HU2). */
export type SupplyAgentFillObjective = 'container' | 'amount' | 'pallet' | 'layer';

/** Resultado de un objetivo de pallets completos o de bases completas (HU2). */
export interface SupplyAgentLogisticsGoal {
  objective: 'pallet' | 'layer';
  target_pallets?: number | null;
  pallets_before?: number | null;
  pallets_after?: number | null;
  /** Productos con la base a medias antes y después del ajuste. */
  lines_open_before?: number;
  lines_open_after?: number;
  reached: boolean;
}

/** Topes de la empresa aplicados al ajuste (FR-013): presupuesto y stock máximo. */
export interface SupplyAgentLogisticsLimits {
  budget_total?: number | null;
  budget_by_supplier?: Record<string, number>;
  stock_capped_products: number;
  /** Productos con tope por caducidad (fecha mínima de sus lotes con stock). */
  shelf_life_products?: number;
}

export interface SupplyAgentLogisticsEconomic {
  objective: 'amount';
  target_amount_euros: number;
  /** `request` si lo escribió el comprador; `supplier_rule` si viene de la regla de importe mínimo del proveedor. */
  target_amount_source?: 'request' | 'supplier_rule' | null;
  saving_euros?: number | null;
  amount_before?: number | null;
  amount_after?: number | null;
  reached: boolean;
}

export interface SupplyAgentLogisticsAlternative {
  strategy: string;
  status?: string;
  summary?: string;
  line_adjustments?: SupplyAgentLogisticsLineAdjustment[];
  warnings?: string[];
  /** Solo en pedidos que exceden el vehículo: reducir al anterior o completar el siguiente. */
  kind?: SupplyAgentOverCapacityOption;
  vehicles?: number;
  selected?: boolean;
  delta_units?: number;
  volume_fill_pct?: number | null;
  weight_fill_pct?: number | null;
}

export interface SupplyAgentLogisticsCalculateResponse {
  mode: string;
  status: string;
  detail?: string;
  organization_id?: number;
  supplier_id?: number | null;
  warehouse_id?: number | null;
  before?: {
    units?: number;
    pallet_equivalent?: number;
    volume_m3?: number;
    weight_kg?: number;
    lines_detail?: Record<string, unknown>[];
  };
  after?: {
    units?: number;
    pallet_equivalent?: number;
    volume_m3?: number;
    weight_kg?: number;
    lines_detail?: Record<string, unknown>[];
  };
  lines_detail?: Record<string, unknown>[];
  changes?: Array<{
    product_id: number;
    sku?: string;
    old_quantity: number;
    new_quantity: number;
    old_value_euros?: number;
    new_value_euros?: number;
    reasons?: Array<{ label?: string; step?: string; rule_type?: string }>;
  }>;
  line_adjustments?: SupplyAgentLogisticsLineAdjustment[];
  alternatives?: SupplyAgentLogisticsAlternative[];
  summary?: SupplyAgentLogisticsOrderSummary | null;
  economic?: SupplyAgentLogisticsEconomic | null;
  goal?: SupplyAgentLogisticsGoal | null;
  limits?: SupplyAgentLogisticsLimits | null;
  suppliers?: SupplyAgentLogisticsSupplierBreakdown[];
  load_mode?: SupplyAgentLoadMode | null;
  shipment_mode?: SupplyAgentShipmentMode | null;
  over_capacity?: {
    vehicles_exact: number;
    vehicles_needed: number;
    option: SupplyAgentOverCapacityOption;
  } | null;
  applied_rules?: Record<string, unknown>[];
  utilization?: {
    pallet_equivalent_before?: number;
    pallet_equivalent_after?: number;
    pallet_fill_pct_after?: number;
    volume_m3_before?: number;
    volume_m3_after?: number;
    volume_fill_pct_after?: number;
    weight_kg_before?: number;
    weight_kg_after?: number;
    weight_fill_pct_after?: number;
    container_target_fill_pct?: number;
    capacity_binding?: string;
    /** Vehículos sobre los que se calculan los porcentajes de la opción elegida. */
    vehicles?: number;
  };
  container?: Record<string, unknown>;
  stowage_plan?: {
    status: 'READY' | 'INCOMPLETE' | 'PARTIAL' | 'BLOCKED' | string;
    complete: boolean;
    container_dimensions_mm: { length_mm: number | null; width_mm: number | null; height_mm: number | null };
    container_payload_limit_kg?: number | null;
    total_pallets?: number;
    placed_pallets: number;
    total_weight_kg?: number;
    placements: Array<{
      product_id: number;
      product_name: string;
      sku: string;
      pallet_index: number;
      pallet_units: number;
      weight_kg: number;
      x_mm: number;
      y_mm: number;
      z_mm: number;
      oriented_length_mm: number;
      oriented_width_mm: number;
      height_mm: number;
      rotated: boolean;
      level: number;
      stack_id: string;
      fragile: boolean;
      stackable: boolean;
    }>;
    unplaced_products: Array<Record<string, unknown>>;
    warnings: string[];
    algorithm: string;
  } | null;
  warnings?: string[];
  missing_data?: Array<{ product_id: number; fields: string[] }>;
  eligible_lines_count?: number;
  skipped_lines_count?: number;
  rule?: Record<string, unknown>;
  applied?: boolean;
  dry_run?: boolean;
  apply_skipped_reason?: string;
  apply_result?: {
    applied?: boolean;
    pending_order_id?: number;
    pending_order_created?: boolean;
    updated_items?: unknown[];
    removed_items?: unknown[];
    [key: string]: unknown;
  };
  audit?: Record<string, unknown>;
}

export interface SupplyAgentContainerTemplate {
  id: number;
  code: string;
  name: string;
  usable_volume_m3: number | null;
  usable_payload_kg: number | null;
}
